import React, { Component, ReactNode } from "react";
import { Box, Alert, AlertTitle, Collapse, IconButton } from "@mui/material";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp
} from "@tabler/icons-react";
import MxTypography from "@rf-l1/MxTypography";

interface Props {
  children: ReactNode;
  sectionName?: string;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDetails: boolean;
}

/**
 * PropertySectionErrorBoundary
 *
 * Error Boundary específico para secciones de la página de detalle de propiedad.
 * Captura errores en componentes hijos y muestra un mensaje de error amigable
 * sin afectar el resto de la página.
 *
 * @example
 * ```tsx
 * <PropertySectionErrorBoundary sectionName="Agentes">
 *   <PropertyAgentsCard />
 * </PropertySectionErrorBoundary>
 * ```
 */
class PropertySectionErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log del error para debugging
    console.error(
      `Error en sección: ${this.props.sectionName || "Desconocida"}`,
      error,
      errorInfo
    );

    this.setState({
      error,
      errorInfo
    });

    // Callback opcional para logging externo
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleToggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  render() {
    if (this.state.hasError) {
      // Si se proporciona un fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Fallback por defecto
      return (
        <Box sx={{ width: "100%", mb: 2 }}>
          <Alert
            severity="warning"
            icon={<IconAlertTriangle size={20} />}
            sx={{
              "& .MuiAlert-message": {
                width: "100%"
              }
            }}
          >
            <AlertTitle>
              <MxTypography variant="subtitle2" fontWeight={600}>
                Error al cargar {this.props.sectionName || "esta sección"}
              </MxTypography>
            </AlertTitle>

            <MxTypography variant="body2" color="text.secondary" mb={1}>
              Ocurrió un problema al cargar esta sección. El resto de la página
              funciona correctamente.
            </MxTypography>

            {/* Botón para mostrar/ocultar detalles técnicos */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    mt: 1
                  }}
                  onClick={this.handleToggleDetails}
                >
                  <MxTypography
                    variant="caption"
                    color="primary.main"
                    fontWeight={600}
                  >
                    {this.state.showDetails ? "Ocultar" : "Ver"} detalles técnicos
                  </MxTypography>
                  <IconButton size="small" sx={{ p: 0 }}>
                    {this.state.showDetails ? (
                      <IconChevronUp size={16} />
                    ) : (
                      <IconChevronDown size={16} />
                    )}
                  </IconButton>
                </Box>

                <Collapse in={this.state.showDetails}>
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      overflow: "auto",
                      maxHeight: "200px"
                    }}
                  >
                    <MxTypography
                      variant="caption"
                      component="div"
                      sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                    >
                      <strong>Error:</strong> {this.state.error.message}
                      {"\n\n"}
                      <strong>Stack:</strong>
                      {"\n"}
                      {this.state.error.stack}
                      {this.state.errorInfo && (
                        <>
                          {"\n\n"}
                          <strong>Component Stack:</strong>
                          {"\n"}
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </MxTypography>
                  </Box>
                </Collapse>
              </>
            )}
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default PropertySectionErrorBoundary;
